import { useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { useAppStore } from "../../state/app-store";
import type { SyncConfig } from "../../ipc/types";
import "../product-development/product-development.css";

/**
 * Configuración de la sincronización programada con Mercado Libre (item 6): activar/desactivar,
 * intervalo en minutos y un botón "Sincronizar ahora". Al guardar reprograma el temporizador global.
 */
export function SyncSettingsPanel(): JSX.Element {
  const client = useIpc();
  const bumpSync = useAppStore((s) => s.bumpSync);
  const { state, reload } = useIpcQuery<SyncConfig>((c) => c.getSyncConfig(), []);
  const [draft, setDraft] = useState<SyncConfig | null>(null);
  const [busy, setBusy] = useState<"save" | "run" | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const cfg = draft ?? (state.status === "ready" ? state.data : null);
  const set = <K extends keyof SyncConfig>(k: K, v: SyncConfig[K]): void => { if (cfg) setDraft({ ...cfg, [k]: v }); };

  const save = async (): Promise<void> => {
    if (!cfg) return;
    setBusy("save"); setMsg(null);
    const res = await client.saveSyncConfig(cfg);
    setBusy(null);
    if (res.ok) { setDraft(null); reload(); bumpSync(); setMsg({ ok: true, text: "Configuración de sincronización guardada." }); }
    else setMsg({ ok: false, text: res.error.message });
  };
  const runNow = async (): Promise<void> => {
    setBusy("run"); setMsg(null);
    const res = await client.syncAll();
    setBusy(null);
    setMsg(res.ok ? { ok: res.value.ok, text: res.value.message } : { ok: false, text: res.error.message });
  };

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="pulse" size={18} /> Sincronización automática</span>}
        subtitle="Importa órdenes y actualiza precio/stock en Mercado Libre cada cierto tiempo, sin apretar botones"
        action={
          <div className="row" style={{ gap: 8 }}>
            <Button variant="secondary" size="sm" icon="marketplace" disabled={busy !== null} onClick={runNow}>{busy === "run" ? "Sincronizando…" : "Sincronizar ahora"}</Button>
            <Button size="sm" icon="check" disabled={busy !== null || !cfg} onClick={save}>{busy === "save" ? "Guardando…" : "Guardar"}</Button>
          </div>
        }
      />
      <CardBody>
        {state.status === "loading" ? <LoadingState label="Cargando…" /> :
         state.status === "error" ? <ErrorState message={state.error.message} onRetry={reload} /> :
         cfg ? (
          <div className="pd-form" style={{ gridTemplateColumns: "1fr 1fr", alignItems: "end" }}>
            <div className="pd-field">
              <span className="pd-field__label">Sincronización automática</span>
              <label className="row" style={{ gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={cfg.enabled} aria-label="Activar sincronización automática" onChange={(e) => set("enabled", e.target.checked)} />
                <span className="muted" style={{ fontSize: 13.5 }}>{cfg.enabled ? "Activada" : "Desactivada"}</span>
              </label>
            </div>
            <div className="pd-field">
              <span className="pd-field__label">Cada cuántos minutos</span>
              <input className="pd-input" type="number" min={5} max={720} step={5} value={cfg.intervalMinutes} aria-label="Intervalo en minutos" disabled={!cfg.enabled} onChange={(e) => set("intervalMinutes", Number(e.target.value))} />
            </div>
          </div>
        ) : null}
        <p className="faint" style={{ fontSize: 12.5, marginTop: "var(--sp-3)" }}>La sincronización automática corre mientras la app está abierta. Los resultados y errores aparecen en el centro de avisos (la campana).</p>
        {msg ? (
          <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
            <Badge tone={msg.ok ? "success" : "danger"} dot>{msg.ok ? "OK" : "Aviso"}</Badge>
            <span className="muted" style={{ fontSize: 13.5 }}>{msg.text}</span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
